import { useEffect, useState, useMemo } from "react";
import {
    Network, Server, Cpu, Activity, Layers, Link2, ChevronLeft, ChevronRight,
    ArrowUpDown, Wifi,
} from "lucide-react";
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Label,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import baseapi from "../../services/baseapi";
import { useClientLevel } from "../../hooks/useClientLevel";
import { showError } from "../../utils/toast";
import { useTheme } from "../../context/ThemeContext";

interface SwitchDetail {
    asset_id: number;
    switch_name: string;
    ip_address: string;
    model: string;
    manufacturer: string;
    ios_version: string;
    serial_number: string;
    location: string;
    uptime: string;
    total_interfaces: number;
    total_ports_up: number;
    total_vlans: number;
    total_mac_entries: number;
    total_cdp_neighbors: number;
}

interface PortStatus {
    port_status: string;
    count: number;
    percentage: number;
}

interface PortUtilization {
    switch_asset_id: number;
    switch_name: string;
    ip_address: string | null;
    total_ports: number;
    ports_up: number;
    utilization_percentage: number;
}

interface Neighbor {
    asset_id: number;
    switch_name: string;
    switch_ip: string;
    protocol: string;
    local_interface_name: string;
    neighbor_device_id: string;
    neighbor_ip_address: string | null;
    neighbor_port: string | null;
    neighbor_platform: string | null;
    neighbor_capabilities: string | null;
    last_seen: string | null;
}

interface VlanEntry {
    asset_id: number;
    switch_name: string;
    vlan_id: number;
    vlan_name: string | null;
    status: string | null;
    vlan_type: string | null;
    egress_port_count: number;
    tagged_port_count: number;
    untagged_port_count: number;
}

interface Inventory {
    totalSwitches: number;
    managedSwitches: number;
    unmanagedSwitches: number;
    uniqueModels: number;
    uniqueManufacturers: number;
    avgInterfaces: number;
    avgPortsUp: number;
    totalVlans: number;
    totalNeighbors: number;

}

interface Topology {
    totalConnections: number;
    uplinkConnections: number;
    standardConnections: number;
    sourceDevices: number;
    destinationDevices: number;
}

interface DashboardData {
    inventory: Inventory;
    details: SwitchDetail[];
    health: any[];
    port_status: PortStatus[];
    port_errors: any[];
    neighbors: Neighbor[];
    vlans: VlanEntry[];
    topology: Topology;
    port_utilization: PortUtilization[];
}

const PORT_COLORS: Record<string, string> = {
    up: "#10b981",
    down: "#f43f5e",
    inactive: "#6b7280",
};

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-2 rounded-lg shadow-xl z-50">
                <p className="text-gray-900 dark:text-gray-100 text-[10px] font-bold mb-1 uppercase tracking-wider">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 text-xs mb-0.5">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry?.color || entry?.payload?.fill }} />
                        <span className="text-gray-600 dark:text-gray-300">{entry?.name}:</span>
                        <span className="text-gray-900 dark:text-white font-mono font-medium">{entry?.value}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const StatMini = ({ icon: Icon, label, value, color, bg }: any) => (
    <div className="flex items-center gap-2 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2 min-w-0 shadow-sm transition-colors">
        <div className={`p-1.5 rounded-lg ${bg} shrink-0`}>
            <Icon className={`w-3.5 h-3.5 ${color}`} />
        </div>
        <div className="min-w-0">
            <p className="text-[9px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest leading-none mb-0.5 truncate">{label}</p>
            <p className="text-sm font-black text-gray-900 dark:text-white leading-none">{value || 0}</p>
        </div>
    </div>
);

const SwitchDashboard = ({ selectedSwitch }: { selectedSwitch: any }) => {
    const currentLevel = useClientLevel();
    const { theme } = useTheme();
    const isDark = theme === "dark";
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<DashboardData | null>(null);

    // Pagination
    const [detailPage, setDetailPage] = useState(0);
    const [neighborPage, setNeighborPage] = useState(0);
    const [vlanPage, setVlanPage] = useState(0);
    const DETAIL_ROWS = 10;
    const NEIGHBOR_ROWS = 10;
    const VLAN_ROWS = 10;

    const isGlobal = currentLevel?.name === "global";

    useEffect(() => {
        if (currentLevel) {
            fetchData();
        }
    }, [currentLevel, selectedSwitch]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const client_id = isGlobal ? null : currentLevel?.id;
            const params: any = {};
            if (client_id) params.client_id = client_id;
            if (selectedSwitch?.id) params.switch_asset_id = selectedSwitch.id;

            const res = await baseapi.get("/dashboard/switch_unified_dashboard", { params });
            if (res?.data?.status) {
                setData(res.data.data);
            }
        } catch (e: any) {
            showError("Failed to fetch switch dashboard data");
        } finally {
            setLoading(false);
        }
    };

    // Port status pie data
    const portPieData = useMemo(() => {
        if (!data?.port_status) return [];
        return data.port_status.map(p => ({
            name: p?.port_status?.charAt(0).toUpperCase() + p?.port_status?.slice(1) || "Unknown",
            value: p?.count || 0,
            color: PORT_COLORS[p?.port_status] || "#6b7280",
        }));
    }, [data]);

    const totalPorts = useMemo(() => portPieData.reduce((s, p) => s + (p?.value || 0), 0), [portPieData]);

    // Port utilization bar data (top 8)
    const portUtilData = useMemo(() => {
        if (!data?.port_utilization) return [];
        return data.port_utilization.slice(0, 8).map(p => ({
            name: p?.switch_name?.length > 12 ? p.switch_name.substring(0, 12) + "…" : (p?.switch_name || "Unknown"),
            fullName: p?.switch_name || "Unknown",
            value: p?.utilization_percentage || 0,
            ip: p?.ip_address || "-",
        }));
    }, [data]);

    // Filtered details / neighbors / vlans
    const filteredDetails = useMemo(() => data?.details || [], [data]);
    const filteredNeighbors = useMemo(() => data?.neighbors || [], [data]);
    const filteredVlans = useMemo(() => data?.vlans || [], [data]);

    const detailTotalPages = Math.ceil(filteredDetails.length / DETAIL_ROWS);
    const neighborTotalPages = Math.ceil(filteredNeighbors.length / NEIGHBOR_ROWS);
    const vlanTotalPages = Math.ceil(filteredVlans.length / VLAN_ROWS);

    const getVisiblePages = (current: number, total: number) => {
        if (total <= 3) return Array.from({ length: total }, (_, i) => i);
        let start = Math.max(0, current - 1);
        if (start + 3 > total) start = total - 3;
        return [start, start + 1, start + 2];
    };

    const Pagination = ({ page, setPage, totalPages }: { page: number; setPage: (p: number) => void; totalPages: number }) => {
        if (totalPages <= 1) return null;
        return (
            <div className="flex items-center justify-end gap-1 pt-1.5">
                <button disabled={page === 0} onClick={() => setPage(Math.max(0, page - 1))}
                    className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 border border-gray-300 dark:border-gray-600 text-[10px] transition-colors">
                    <ChevronLeft className="w-3 h-3" />
                </button>
                {getVisiblePages(page, totalPages).map(pi => (
                    <button key={pi} onClick={() => setPage(pi)}
                        className={`w-5 h-5 text-[9px] font-bold rounded transition-colors ${pi === page ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'}`}>
                        {pi + 1}
                    </button>
                ))}
                <button disabled={page >= totalPages - 1} onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 border border-gray-300 dark:border-gray-600 text-[10px] transition-colors">
                    <ChevronRight className="w-3 h-3" />
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-full flex flex-col items-center justify-center text-gray-700 dark:text-gray-100 py-12">
                <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin mb-3" />
                <p className="text-[11px] font-bold uppercase tracking-widest animate-pulse">Loading Switch Dashboard...</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-full flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm py-12">No data available</div>
        );
    }

    // Safely extract inventory with fallback default values
    const inv = data?.inventory || {
        totalSwitches: 0,
        uniqueModels: 0,
        uniqueManufacturers: 0,
        avgInterfaces: 0,
        avgPortsUp: 0,
        totalVlans: 0,
        totalNeighbors: 0
    };

    return (
        <div className="min-h-full xl:h-full bg-slate-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-sans flex flex-col">

            {/* Inventory Stats */}
            <div className="flex-none px-2 pt-1 pb-1 z-20">

                {/* Inventory stat pills */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-9 gap-1.5">
                    <StatMini icon={Network} label="Total Switches" value={inv?.totalSwitches || 0} color="text-orange-500 dark:text-orange-400" bg="bg-orange-500/10" />
                    <StatMini icon={Network} label="Managed Switches" value={inv?.managedSwitches || 0} color="text-orange-500 dark:text-orange-400" bg="bg-orange-500/10" />
                    <StatMini icon={Network} label="Unmanaged Switches" value={inv?.unmanagedSwitches || 0} color="text-orange-500 dark:text-orange-400" bg="bg-orange-500/10" />
                    <StatMini icon={Server} label="Unique Models" value={inv?.uniqueModels || 0} color="text-blue-500 dark:text-blue-400" bg="bg-blue-500/10" />
                    <StatMini icon={Layers} label="Manufacturers" value={inv?.uniqueManufacturers || 0} color="text-violet-500 dark:text-violet-400" bg="bg-violet-500/10" />
                    <StatMini icon={Activity} label="Avg Interfaces" value={inv?.avgInterfaces?.toFixed(1) || "0.0"} color="text-cyan-600 dark:text-cyan-400" bg="bg-cyan-500/10" />
                    <StatMini icon={Wifi} label="Avg Ports Up" value={inv?.avgPortsUp?.toFixed(1) || "0.0"} color="text-emerald-600 dark:text-emerald-400" bg="bg-emerald-500/10" />
                    <StatMini icon={ArrowUpDown} label="Total VLANs" value={inv?.totalVlans || 0} color="text-amber-500 dark:text-amber-400" bg="bg-amber-500/10" />
                    <StatMini icon={Link2} label="Neighbors" value={inv?.totalNeighbors || 0} color="text-pink-500 dark:text-pink-400" bg="bg-pink-500/10" />
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col px-2 py-1 gap-2">

                {/* TOP ROW: Port Status Pie + Port Utilization Bar */}
                <div className="flex-none xl:flex-1 min-h-0 flex flex-col xl:flex-row gap-2">

                    {/* Port Status Pie */}
                    <div className="w-full xl:w-1/3 min-h-[320px] xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm">
                        <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5 mb-1">
                            <div className="p-1 rounded bg-emerald-500/10 text-emerald-500 dark:text-emerald-400"><Activity className="w-3 h-3" /></div>
                            Port Status
                        </h3>
                        <div className="h-[240px] xl:h-full xl:flex-1 w-full min-h-[220px]">
                            {portPieData.length === 0 ? (
                                <div className="h-full flex items-center justify-center text-[10px] text-gray-400 dark:text-gray-500">No data</div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={portPieData} innerRadius="50%" outerRadius="78%" paddingAngle={3} dataKey="value" cornerRadius={4}>
                                            {portPieData.map((e, i) => <Cell key={i} fill={e?.color || "#6b7280"} stroke="none" />)}
                                            <Label
                                                content={({ viewBox: { cx, cy } }: any) => (
                                                    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                                                        <tspan x={cx} dy="-0.1em" fill={isDark ? "#f8fafc" : "#0f172a"} fontSize="16" fontWeight="bold">{totalPorts}</tspan>
                                                        <tspan x={cx} dy="1.3em" fill={isDark ? "#9ca3af" : "#64748b"} fontSize="8" fontWeight="600" className="uppercase tracking-wider">PORTS</tspan>
                                                    </text>
                                                )}
                                            />
                                        </Pie>
                                        <Tooltip content={<CustomTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-1">
                            {portPieData.map((e, i) => (
                                <div key={i} className="flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e?.color || "#6b7280" }} />
                                    <span className="text-[8px] text-gray-600 dark:text-gray-300 font-bold uppercase">{e?.name || "Unknown"} ({e?.value || 0})</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Port Utilization Bar */}
                    <div className="w-full xl:w-2/3 min-h-[360px] xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm">
                        <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5 mb-1">
                            <div className="p-1 rounded bg-blue-500/10 text-blue-500 dark:text-blue-400"><Cpu className="w-3 h-3" /></div>
                            Port Utilization
                        </h3>
                        <div className="h-[300px] xl:h-full xl:flex-1 w-full min-h-[260px]">
                            {portUtilData.length === 0 ? (
                                <div className="h-full flex items-center justify-center text-[10px] text-gray-400 dark:text-gray-500">No data</div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={portUtilData} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }} barCategoryGap="16%">
                                        <defs>
                                            <linearGradient id="swBarNorm" x1="0" y1="0" x2="1" y2="0">
                                                <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.3} />
                                                <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0.9} />
                                            </linearGradient>
                                            <linearGradient id="swBarWarn" x1="0" y1="0" x2="1" y2="0">
                                                <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                                                <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.9} />
                                            </linearGradient>
                                            <linearGradient id="swBarCrit" x1="0" y1="0" x2="1" y2="0">
                                                <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.3} />
                                                <stop offset="100%" stopColor="#f43f5e" stopOpacity={0.9} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} horizontal={false} />
                                        <XAxis type="number" domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: isDark ? '#9ca3af' : '#64748b', fontSize: 8 }} tickCount={5} />
                                        <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} width={100} interval={0}
                                            tick={({ x, y, payload }: any) => (
                                                <text x={x - 4} y={y} textAnchor="end" dominantBaseline="central" fill={isDark ? "#f5f6f7" : "#334155"} fontSize={8}>
                                                    <title>{payload?.value}</title>
                                                    {payload?.value}
                                                </text>
                                            )}
                                        />
                                        <Tooltip content={({ active, payload }: any) => {
                                            if (!active || !payload?.length) return null;
                                            const d = payload[0]?.payload;
                                            return (
                                                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow-xl">
                                                    <p className="text-[10px] text-gray-900 dark:text-gray-100 font-bold mb-0.5">{d?.fullName || "Unknown"}</p>
                                                    <p className="text-[9px] text-gray-500 dark:text-gray-400">IP: {d?.ip || "-"}</p>
                                                    <p className="text-[10px] font-mono font-bold text-blue-500 dark:text-blue-400">{d?.value || 0}%</p>
                                                </div>
                                            );
                                        }} cursor={{ fill: isDark ? '#334155' : '#e2e8f0', opacity: 0.3 }} />
                                        <Bar dataKey="value" radius={[0, 4, 4, 0]}
                                            label={({ x, y, width: w, height: h, value }: any) => (
                                                <text x={x + w + 4} y={y + h / 2} dominantBaseline="central"
                                                    fill={value > 90 ? '#f43f5e' : value > 70 ? '#f59e0b' : '#0ea5e9'}
                                                    fontSize={8} fontWeight="bold" fontFamily="monospace">
                                                    {value || 0}%
                                                </text>
                                            )}>
                                            {portUtilData.map((entry, i) => (
                                                <Cell key={i} fill={(entry?.value || 0) > 90 ? 'url(#swBarCrit)' : (entry?.value || 0) > 70 ? 'url(#swBarWarn)' : 'url(#swBarNorm)'} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>

                {/* BOTTOM ROW: Switch Details + Neighbors + VLANs */}
                <div className="flex-none xl:flex-[1.2] min-h-0 flex flex-col xl:flex-row gap-2 pb-4 xl:pb-0">

                    {/* Switch Details Table */}
                    <div className="w-full xl:w-[45%] h-[380px] xl:h-auto xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm">
                        <div className="flex items-center justify-between mb-1.5 flex-none">
                            <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5">
                                <div className="p-1 rounded bg-orange-500/10 text-orange-500 dark:text-orange-400"><Server className="w-3 h-3" /></div>
                                Switch Inventory ({filteredDetails.length})
                            </h3>
                        </div>
                        <div className="flex-1 min-h-0 flex flex-col overflow-x-auto custom-scroll">
                            <div className="min-w-[620px] flex-1 min-h-0 flex flex-col">
                                {/* Header */}
                                <div className="grid grid-cols-12 gap-1 px-2 py-1.5 text-[8px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 shrink-0">
                                    <div className="col-span-2">Name</div>
                                    <div className="col-span-2">IP</div>
                                    <div className="col-span-2">Model</div>
                                    <div className="col-span-1">Vendor</div>
                                    <div className="col-span-2">Uptime</div>
                                    <div className="col-span-1 text-center">Ports</div>
                                    <div className="col-span-1 text-center">VLANs</div>
                                    <div className="col-span-1 text-center">MACs</div>
                                </div>
                                <div className="flex-1 overflow-y-auto custom-scroll">
                                    {filteredDetails.slice(detailPage * DETAIL_ROWS, (detailPage + 1) * DETAIL_ROWS).map((sw, idx) => (
                                        <div key={sw?.asset_id || idx}
                                            className="grid grid-cols-12 gap-1 px-2 py-1.5 border-b border-gray-100 dark:border-gray-700/40 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer items-center"
                                        >
                                            <div className="col-span-2 text-[9px] text-gray-900 dark:text-gray-100 font-semibold truncate" title={sw?.switch_name}>{sw?.switch_name || "—"}</div>
                                            <div className="col-span-2 text-[9px] text-gray-600 dark:text-gray-300 font-mono truncate">{sw?.ip_address || "—"}</div>
                                            <div className="col-span-2 text-[9px] text-gray-700 dark:text-gray-300 truncate" title={sw?.model}>{sw?.model || "—"}</div>
                                            <div className="col-span-1 text-[9px] text-gray-700 dark:text-gray-300 truncate" title={sw?.manufacturer}>{sw?.manufacturer || "—"}</div>
                                            <div className="col-span-2 text-[9px] text-gray-600 dark:text-gray-400 truncate" title={sw?.uptime}>{sw?.uptime || "—"}</div>
                                            <div className="col-span-1 text-center">
                                                <span className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400">{sw?.total_ports_up || 0}</span>
                                                <span className="text-[8px] text-gray-400">/{sw?.total_interfaces || 0}</span>
                                            </div>
                                            <div className="col-span-1 text-center text-[9px] text-cyan-600 dark:text-cyan-400 font-bold">{sw?.total_vlans || 0}</div>
                                            <div className="col-span-1 text-center text-[9px] text-amber-600 dark:text-amber-400 font-bold">{sw?.total_mac_entries || 0}</div>
                                        </div>
                                    ))}
                                </div>
                                <Pagination page={detailPage} setPage={setDetailPage} totalPages={detailTotalPages} />
                            </div>
                        </div>
                    </div>

                    {/* Neighbors Table */}
                    <div className="w-full xl:w-[30%] h-[380px] xl:h-auto xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm">
                        <div className="flex items-center justify-between mb-1.5 flex-none">
                            <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5">
                                <div className="p-1 rounded bg-pink-500/10 text-pink-500 dark:text-pink-400"><Link2 className="w-3 h-3" /></div>
                                Neighbors ({filteredNeighbors.length})
                            </h3>
                        </div>
                        <div className="flex-1 min-h-0 flex flex-col overflow-x-auto custom-scroll">
                            <div className="min-w-[420px] flex-1 min-h-0 flex flex-col">
                                <div className="grid grid-cols-12 gap-1 px-2 py-1.5 text-[8px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 shrink-0">
                                    <div className="col-span-2">Switch</div>
                                    <div className="col-span-2">Protocol</div>
                                    <div className="col-span-3">Local Intf</div>
                                    <div className="col-span-3">Neighbor</div>
                                    <div className="col-span-2">Platform</div>
                                </div>
                                <div className="flex-1 overflow-y-auto custom-scroll">
                                    {filteredNeighbors.slice(neighborPage * NEIGHBOR_ROWS, (neighborPage + 1) * NEIGHBOR_ROWS).map((n, idx) => (
                                        <div key={`${n?.asset_id}-${n?.local_interface_name}-${idx}`}
                                            className="grid grid-cols-12 gap-1 px-2 py-1.5 border-b border-gray-100 dark:border-gray-700/40 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors items-center">
                                            <div className="col-span-2 text-[9px] text-gray-900 dark:text-gray-100 font-semibold truncate" title={n?.switch_name}>{n?.switch_name || "—"}</div>
                                            <div className="col-span-2">
                                                <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded ${n?.protocol?.toLowerCase() === 'cdp' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'}`}>
                                                    {n?.protocol || "—"}
                                                </span>
                                            </div>
                                            <div className="col-span-3 text-[9px] text-gray-700 dark:text-gray-300 truncate" title={n?.local_interface_name}>{n?.local_interface_name || "—"}</div>
                                            <div className="col-span-3 text-[9px] text-gray-700 dark:text-gray-300 truncate" title={n?.neighbor_device_id || "unknown"}>{n?.neighbor_device_id || "unknown"}</div>
                                            <div className="col-span-2 text-[8px] text-gray-600 dark:text-gray-400 truncate" title={n?.neighbor_platform || "—"}>{n?.neighbor_platform ? (n.neighbor_platform.length > 20 ? n.neighbor_platform.substring(0, 20) + "…" : n.neighbor_platform) : "—"}</div>
                                        </div>
                                    ))}
                                </div>
                                <Pagination page={neighborPage} setPage={setNeighborPage} totalPages={neighborTotalPages} />
                            </div>
                        </div>
                    </div>

                    {/* VLANs Table */}
                    <div className="w-full xl:w-[25%] h-[380px] xl:h-auto xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm">
                        <div className="flex items-center justify-between mb-1.5 flex-none">
                            <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5">
                                <div className="p-1 rounded bg-cyan-500/10 text-cyan-500 dark:text-cyan-400"><Layers className="w-3 h-3" /></div>
                                VLANs ({filteredVlans.length})
                            </h3>
                        </div>
                        <div className="flex-1 min-h-0 flex flex-col overflow-x-auto custom-scroll">
                            <div className="min-w-[360px] flex-1 min-h-0 flex flex-col">
                                <div className="grid grid-cols-12 gap-1 px-2 py-1.5 text-[8px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 shrink-0">
                                    <div className="col-span-2">ID</div>
                                    <div className="col-span-3">Name</div>
                                    <div className="col-span-3">Switch</div>
                                    <div className="col-span-2">Type</div>
                                    <div className="col-span-2 text-center">Ports</div>
                                </div>
                                <div className="flex-1 overflow-y-auto custom-scroll">
                                    {filteredVlans.slice(vlanPage * VLAN_ROWS, (vlanPage + 1) * VLAN_ROWS).map((v, idx) => (
                                        <div key={`${v?.asset_id}-${v?.vlan_id}-${idx}`}
                                            className="grid grid-cols-12 gap-1 px-2 py-1.5 border-b border-gray-100 dark:border-gray-700/40 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors items-center">
                                            <div className="col-span-2 text-[9px] text-cyan-600 dark:text-cyan-400 font-bold">{v?.vlan_id || "—"}</div>
                                            <div className="col-span-3 text-[9px] text-gray-700 dark:text-gray-300 truncate" title={v?.vlan_name || "—"}>{v?.vlan_name || "—"}</div>
                                            <div className="col-span-3 text-[9px] text-gray-700 dark:text-gray-300 truncate" title={v?.switch_name}>{v?.switch_name || "—"}</div>
                                            <div className="col-span-2 text-[8px] text-gray-600 dark:text-gray-400 truncate">{v?.vlan_type || "—"}</div>
                                            <div className="col-span-2 text-center text-[9px] text-gray-700 dark:text-gray-200 font-mono">{v?.egress_port_count || 0}</div>
                                        </div>
                                    ))}
                                </div>
                                <Pagination page={vlanPage} setPage={setVlanPage} totalPages={vlanTotalPages} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SwitchDashboard;